'use client';

import { Suspense, useEffect, useRef, useState } from 'react';
import NextImage from 'next/image';
import SalesCallbackProvider from '@/components/sales-callback/SalesCallbackProvider';
import SalesCallbackSection from '@/components/sales-callback/SalesCallbackSection';
import LandmarkProjectsList from './components/LandmarkProjectsList';
import LandmarkProjectListSkeleton from './components/LandmarkProjectListSkeleton';
import LandmarkVoicesOfTrust from './components/LandmarkVoicesOfTrust';
import Container from '@/components/ui/Container/Container';
import FaqEnquirySection from '@/components/sections/Products/FaqEnquirySection';
import type { InfraLandingData } from './server/mapProjectPage';
import type { LeadForm } from '@/types/lead-forms';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import type { ContactForm } from '@/types/contact-forms';
import CmsRichTextHeading from '@/components/ui/CmsRichTextHeading';

const HERO_VIDEO_FALLBACK = '/images/proj_img1.mp4';

const HERO_DESCRIPTION =
  "Wonder Cement powers India's most ambitious infrastructure, from the Dedicated Freight Corridor to the Mumbai Coastal Road, built to move the nation forward.";

interface LandmarkProjectsClientProps {
  landing: InfraLandingData | null;
  locale: string;
  leadForms?: LeadForm[];
  emailContactForm?: ContactForm | null;
}

export default function LandmarkProjectsClient({
  landing,
  locale,
  leadForms,
  emailContactForm,
}: LandmarkProjectsClientProps) {
  const mobileVideoRef = useRef<HTMLVideoElement>(null);
  const desktopVideoRef = useRef<HTMLVideoElement>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);

  const heroTitle = landing?.hero.title?.trim() || 'Infrastructure Milestones';
  const heroDescriptions = landing?.hero.paragraphsHtml.filter((description) => description.trim());
  const heroVideo = landing?.hero.videoUrl || HERO_VIDEO_FALLBACK;
  const heroPoster = landing?.hero.posterUrl || MEDIA_PLACEHOLDER;

  const heroTextContent = (
    <>
      <CmsRichTextHeading
        className="max-w-[18ch] hero-title text-white"
        html={landing?.hero.titleRichText}
        fallback={heroTitle}
      />
      {heroDescriptions?.length ? (
        <div className="hero-subtitle max-w-3xl text-left text-white/95 [&_p]:m-0 [&_p+p]:mt-4">
          {heroDescriptions.map((description) => (
            <div key={description} dangerouslySetInnerHTML={{ __html: description }} />
          ))}
        </div>
      ) : (
        <p className="hero-subtitle max-w-3xl text-left text-white/95">{HERO_DESCRIPTION}</p>
      )}
    </>
  );

  // Some browsers (mobile Safari low-power mode, data saver, etc.) silently
  // block autoplay. Explicitly call play() once the element is mounted, and
  // if it still fails, leave the poster image showing instead of a black box.
  useEffect(() => {
    setIsVideoReady(false);
    const videos = [mobileVideoRef.current, desktopVideoRef.current].filter(
      (video): video is HTMLVideoElement => video != null,
    );
    if (!videos.length) return;

    const markReady = () => setIsVideoReady(true);
    const markFailed = () => setIsVideoReady(false);

    const tryPlay = (video: HTMLVideoElement) => {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          const retry = () => {
            video.play().catch(() => undefined);
            document.removeEventListener('pointerdown', retry);
            document.removeEventListener('touchstart', retry);
          };
          document.addEventListener('pointerdown', retry, { once: true });
          document.addEventListener('touchstart', retry, { once: true });
        });
      }
    };

    videos.forEach((video) => {
      if (video.readyState >= 2) {
        markReady();
      }

      video.addEventListener('loadeddata', markReady);
      video.addEventListener('canplay', markReady);
      video.addEventListener('playing', markReady);
      video.addEventListener('error', markFailed);
      tryPlay(video);
    });

    return () => {
      videos.forEach((video) => {
        video.removeEventListener('loadeddata', markReady);
        video.removeEventListener('canplay', markReady);
        video.removeEventListener('playing', markReady);
        video.removeEventListener('error', markFailed);
      });
    };
  }, [heroVideo]);

  return (
    <SalesCallbackProvider variant="corporate" formConfig={emailContactForm}>
      <SalesCallbackSection mode="page">
        <div className="min-h-screen bg-black">
          {/* Mobile / tablet: poster or video first, then content below. */}
          <div className="bg-black lg:hidden">
            <div className="relative w-full">
              <NextImage
                src={MEDIA_PLACEHOLDER}
                alt=""
                aria-hidden
                width={1920}
                height={1080}
                priority
                unoptimized
                className="block h-auto w-full object-cover"
                sizes="100vw"
              />
              <video
                ref={mobileVideoRef}
                key={`mobile-${heroVideo}`}
                poster={heroPoster}
                className={`pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
                  isVideoReady ? 'opacity-100' : 'opacity-0'
                }`}
                autoPlay
                muted
                loop
                playsInline
                controls={false}
                disablePictureInPicture
                preload="metadata"
                aria-hidden
              >
                <source src={heroVideo} type="video/mp4" />
              </video>
            </div>
            <Container>
              <div className="flex flex-col items-start gap-8 py-10 text-white sm:py-12 lg:gap-10">
                {heroTextContent}
              </div>
            </Container>
          </div>

          {/* Desktop: full-bleed overlay hero. */}
          <section className="relative isolate hidden min-h-screen overflow-hidden bg-black lg:block">
            <div className="absolute inset-0 z-0 overflow-hidden">
              <NextImage
                src={MEDIA_PLACEHOLDER}
                alt=""
                aria-hidden
                fill
                priority
                unoptimized
                className="object-cover object-center"
                sizes="100vw"
              />
              <video
                ref={desktopVideoRef}
                key={`desktop-${heroVideo}`}
                poster={heroPoster}
                className={`pointer-events-none absolute left-1/2 top-1/2 h-[calc(100%+8px)] w-[calc(100%+8px)] -translate-x-1/2 -translate-y-1/2 object-cover transition-opacity duration-700 ${
                  isVideoReady ? 'opacity-100' : 'opacity-0'
                }`}
                autoPlay
                muted
                loop
                playsInline
                controls={false}
                disablePictureInPicture
                // `preload="metadata"` (not "auto"): only fetch headers (few KB)
                // on initial paint, not the full multi-MB MP4. The poster is the
                // LCP element on mobile (Lighthouse 5.5s → measurement) and was
                // being starved of bandwidth by the eager video download.
                // Autoplay still works — the explicit `.play()` in the useEffect
                // above kicks in once metadata is ready and starts buffering on
                // demand, so the video appears within a beat after the poster.
                preload="metadata"
                aria-hidden
              >
                <source src={heroVideo} type="video/mp4" />
              </video>
            </div>
            <div
              className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-t from-black via-black/75 to-black/35"
              aria-hidden
            />
            <div className="relative z-[2] container mx-auto flex min-h-screen flex-col justify-end py-12 md:py-14 lg:py-16">
              <div className="flex w-full flex-col items-start gap-8 lg:gap-10 max-sm:px-4">
                {heroTextContent}
              </div>
            </div>
          </section>

          <div className="-mt-px bg-[#f8f7f4] text-black">
            <Suspense fallback={<LandmarkProjectListSkeleton />}>
              <LandmarkProjectsList
                projects={landing?.milestones.projects ?? []}
                heading={landing?.milestones.heading}
                subheadingHtml={landing?.milestones.subheadingHtml ?? null}
                locale={locale}
              />
            </Suspense>
          </div>

          <LandmarkVoicesOfTrust
            title={landing?.testimonials.title}
            items={landing?.testimonials.items ?? []}
            quoteIconUrl={landing?.testimonials.quoteIconUrl ?? null}
          />

          {landing?.faqComponent ? (
            <FaqEnquirySection faqComponent={landing.faqComponent} leadForms={leadForms} />
          ) : null}
        </div>
      </SalesCallbackSection>
    </SalesCallbackProvider>
  );
}
